/**
 * 공통 QA 가져오기 — 이 현장에 아직 없는 공통 항목을 보여 주고, 항목마다 QA 가능/불가를
 * 고른 뒤 가져온다. 불가는 사유 필수(백엔드도 거절한다). 기본은 전부 가능.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { QaImportChoice, QaTemplateItem, SiteQaService } from '../../services/SiteQaService';

interface Props {
  open: boolean;
  /** 이미 현장에 있는 공통 항목 id — 목록에서 뺀다. */
  existingTemplateIds: number[];
  onClose: () => void;
  onImport: (choices: QaImportChoice[]) => Promise<void>;
}

interface Choice {
  applicable: boolean;
  reason: string;
}

const QaImportDialog: React.FC<Props> = ({ open, existingTemplateIds, onClose, onImport }) => {
  const [items, setItems] = useState<QaTemplateItem[] | null>(null);
  const [choices, setChoices] = useState<Record<number, Choice>>({});
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [tried, setTried] = useState(false);
  // 열 때의 목록만 쓴다 — 가져온 직후 부모 목록이 바뀌어도 다시 불러오지 않게.
  const existingRef = useRef(existingTemplateIds);
  existingRef.current = existingTemplateIds;

  useEffect(() => {
    if (!open) return;
    let alive = true;
    setItems(null);
    setChoices({});
    setError(null);
    setTried(false);
    const have = new Set(existingRef.current);
    SiteQaService.listTemplate()
      .then((all) => {
        if (!alive) return;
        const fresh = all.filter((t) => !have.has(t.id));
        setItems(fresh);
        setChoices(Object.fromEntries(fresh.map((t) => [t.id, { applicable: true, reason: '' }])));
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : String(e)));
    return () => {
      alive = false;
    };
  }, [open]);

  const unavailable = useMemo(
    () => Object.values(choices).filter((c) => !c.applicable).length,
    [choices]
  );
  const missingReason = useMemo(
    () => Object.values(choices).some((c) => !c.applicable && !c.reason.trim()),
    [choices]
  );

  const setChoice = (id: number, patch: Partial<Choice>) =>
    setChoices((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));

  const submit = async () => {
    setTried(true);
    if (missingReason) {
      setError('QA 불가로 고른 항목에 사유를 적어 주세요');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await onImport(
        Object.entries(choices).map(([id, c]) => ({
          template_item_id: Number(id),
          applicable: c.applicable,
          unavailable_reason: c.applicable ? '' : c.reason.trim(),
        }))
      );
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>
        공통 QA 가져오기
        <Typography variant="body2" color="text.secondary">
          이 현장에서 진행할 수 없는 항목은 <b>불가</b>로 바꾸고 사유를 적어 주세요.
        </Typography>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0 }}>
        {error && (
          <Alert severity="error" sx={{ m: 2 }}>
            {error}
          </Alert>
        )}
        {items === null ? (
          !error && (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={28} />
            </Box>
          )
        ) : items.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 5, textAlign: 'center' }}>
            새로 가져올 공통 QA 항목이 없습니다. 이미 모두 가져왔습니다.
          </Typography>
        ) : (
          <Stack divider={<Divider flexItem />} data-testid="qa-import-list">
            {items.map((t) => {
              const c = choices[t.id] ?? { applicable: true, reason: '' };
              return (
                <Box key={t.id} sx={{ px: 3, py: 1.5 }}>
                  <Stack direction="row" spacing={2} alignItems="flex-start">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {t.category && (
                          <Typography component="span" variant="body2" color="text.secondary" sx={{ mr: 1 }}>
                            {t.category}
                          </Typography>
                        )}
                        {t.title}
                      </Typography>
                      {t.method && (
                        <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                          {t.method}
                        </Typography>
                      )}
                    </Box>
                    <ToggleButtonGroup
                      exclusive
                      size="small"
                      value={c.applicable ? 'yes' : 'no'}
                      onChange={(_, v) => v && setChoice(t.id, { applicable: v === 'yes' })}
                      sx={{ flexShrink: 0 }}
                    >
                      <ToggleButton value="yes" color="primary" data-testid={`qa-import-yes-${t.id}`} sx={{ px: 2 }}>
                        가능
                      </ToggleButton>
                      <ToggleButton value="no" color="warning" data-testid={`qa-import-no-${t.id}`} sx={{ px: 2 }}>
                        불가
                      </ToggleButton>
                    </ToggleButtonGroup>
                  </Stack>
                  {!c.applicable && (
                    <TextField
                      placeholder="불가 사유 (예: 비상벨 미설치 현장)"
                      value={c.reason}
                      onChange={(e) => setChoice(t.id, { reason: e.target.value })}
                      size="small"
                      fullWidth
                      autoFocus
                      error={tried && !c.reason.trim()}
                      sx={{ mt: 1 }}
                      inputProps={{ 'data-testid': `qa-import-reason-${t.id}` }}
                    />
                  )}
                </Box>
              );
            })}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3 }}>
        {items && items.length > 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ mr: 'auto' }}>
            {items.length}개 중 가능 {items.length - unavailable} · 불가 {unavailable}
          </Typography>
        )}
        <Button onClick={onClose} disabled={saving}>
          {items && items.length === 0 ? '닫기' : '취소'}
        </Button>
        {items && items.length > 0 && (
          <Button variant="contained" disableElevation onClick={submit} disabled={saving} data-testid="qa-import-submit">
            {items.length}개 가져오기
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default QaImportDialog;
